import { ccfRoutes } from '@app/ccf/ccf-routes';
import Navbar from '@app/components/layout/nav/navbar';
import Page from '@app/components/layout/page/page';
import { GoogleMapsApiProvider } from '@app/services/google/google-maps-api-provider';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  useCreateSupplyChainActivity,
  useCreateSupplyChainFlow,
  useDeleteSupplyChainActivity,
  useGetSupplyChainFlow,
  useUpdateSupplyChainFlow,
} from '@shared/api/hooks/ccf/activities/supply-chain-logistics';
import { Form } from '@shared/components/ui/form';
import { useToast } from '@shared/components/ui/use-toast';
import sentry from '@shared/services/sentry';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import {
  generatePath,
  Outlet,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom';
import { z } from 'zod';
import { useCCF } from '../ccf-context';
import { reportSlugs } from '../slugs';
import { supplyFlowSchema } from './schema';

export type SupplyFlowFormValues = z.infer<typeof supplyFlowSchema>;

export type SupplyFlowContext = {
  flowUuid?: string;
  isCreating?: boolean;
  updateMode?: boolean;
  status: 'idle' | 'loading' | 'success' | 'error';
  reportUuid: string;
  loadingFlow?: boolean;
  onSubmit: (data: SupplyFlowFormValues) => Promise<void>;
  onDelete: () => Promise<void>;
  isDeleting?: boolean;
};

export default function SupplyFlowLayout() {
  const { flow_uuid: flowUuid, report_uuid: companyReportUuid } = useParams();
  const navigate = useNavigate();
  const { categories } = useCCF();
  const { pathname } = useLocation();
  const { toast } = useToast();

  const supplyChainLogistics = categories?.find(
    (item) => item.slug === reportSlugs.supplyChainLogistics
  );

  const updateMode = !!flowUuid;

  const {
    mutateAsync: createFlow,
    isLoading: isCreatingFlow,
    status: createFlowStatus,
  } = useCreateSupplyChainFlow();

  const { mutateAsync: updateFlow, isLoading: isUpdatingFlow } =
    useUpdateSupplyChainFlow({
      companyReportUuid: companyReportUuid!,
    });

  const { mutateAsync: deleteSupplyFlow, isLoading: isDeleting } =
    useDeleteSupplyChainActivity(companyReportUuid!);

  const {
    mutateAsync: createSupplyChainActivity,
    isLoading: isCreatingSuppleChainActivity,
  } = useCreateSupplyChainActivity();

  const { data: supplyFlow, isLoading: loadingFlow } = useGetSupplyChainFlow(
    flowUuid!
  );

  const form = useForm<SupplyFlowFormValues>({
    resolver: zodResolver(supplyFlowSchema),
  });

  const isCreating =
    isCreatingSuppleChainActivity || isCreatingFlow || isUpdatingFlow;

  const onDelete = async () => {
    try {
      if (flowUuid && companyReportUuid && supplyFlow?.uuid) {
        await deleteSupplyFlow({
          logisticsFlowUuid: supplyFlow.uuid,
        });
        toast({
          title: 'Supply flow deleted',
          variant: 'success',
        });
        navigate(
          generatePath(ccfRoutes.SUPPLY_CHAIN_LOGISTICS, {
            report_uuid: companyReportUuid,
          })
        );
      }
    } catch (error) {
      sentry.log(error);
      toast({
        title: 'Error deleting flow',
        description: 'Please try again',
        variant: 'destructive',
      });
    }
  };

  const onSubmit = async (data: SupplyFlowFormValues) => {
    try {
      // Map 'international' to null for API compatibility
      const typeOfSuppliers =
        data.typeOfSuppliers === 'international' ? null : data.typeOfSuppliers;

      if (updateMode) {
        await updateFlow({
          logisticsFlowUuid: flowUuid,
          data: {
            ...data,
            typeOfSuppliers,
            name: data.name,
            originLocation: data.originLocation || null,
            weight:
              typeof data.weight === 'string'
                ? Number(data.weight)
                : data.weight,
            coldChainPercentage:
              typeof data.coldChainPercentage === 'string'
                ? Number(data.coldChainPercentage)
                : data.coldChainPercentage,
            segments: data.segments.map((segment) => ({
              ...segment,
              storageManagedBy: 'supplier' as const,
              transportManagedBy: 'supplier' as const,
              distance:
                typeof segment.distance === 'string'
                  ? Number(segment.distance)
                  : segment.distance,
            })),
          },
        });
      } else {
        const { uuid: flowUuid } = await createFlow({
          data: {
            ...data,
            typeOfSuppliers,
            originLocation: data.originLocation,
            weight:
              typeof data.weight === 'string'
                ? Number(data.weight)
                : data.weight,
            coldChainPercentage:
              typeof data.coldChainPercentage === 'string'
                ? Number(data.coldChainPercentage)
                : data.coldChainPercentage,
            segments: data.segments.map((segment) => ({
              ...segment,
              storageManagedBy: 'supplier' as const,
              transportManagedBy: 'supplier' as const,
              distance:
                typeof segment.distance === 'string'
                  ? Number(segment.distance)
                  : segment.distance,
            })),
          },
        });

        await createSupplyChainActivity({
          companyReportUuid: companyReportUuid!,
          data: {
            logisticsFlow: flowUuid,
          },
        });
      }

      toast({
        title: `Supply flow ${updateMode ? 'updated' : 'created'}  successfully`,
        variant: 'success',
      });

      navigate(
        generatePath(ccfRoutes.SUPPLY_CHAIN_LOGISTICS, {
          report_uuid: companyReportUuid!,
        })
      );
    } catch (error) {
      sentry.log(error);
      toast({
        title: 'Error creating flow',
        description: 'Please ensure all values are complete.',
        variant: 'destructive',
      });
    }
  };

  const context: SupplyFlowContext = {
    reportUuid: companyReportUuid!,
    status: createFlowStatus,
    flowUuid,
    isCreating,
    updateMode,
    onSubmit,
    loadingFlow,
    onDelete,
    isDeleting,
  };

  useEffect(() => {
    const typeOfSuppliers = form.getValues('typeOfSuppliers');
    const supplierProximity = form.getValues('suppliersProximity');

    if (
      !pathname.includes('/intro') &&
      !pathname.includes('/about-suppliers') &&
      (!typeOfSuppliers || !supplierProximity) &&
      !updateMode
    ) {
      navigate(
        generatePath(ccfRoutes.SUPPLY_FLOW_INTRO, {
          report_uuid: companyReportUuid,
          flow_uuid: flowUuid || '',
        })
      );
    }
  }, [companyReportUuid, form, navigate, pathname, flowUuid, updateMode]);

  useEffect(() => {
    if (supplyFlow) {
      form.reset({
        name: supplyFlow.name,
        // Map null typeOfSuppliers back to 'international' for the form
        typeOfSuppliers: supplyFlow.typeOfSuppliers || 'international',
        type: 'supply' as const,
        suppliersProximity: supplyFlow.suppliersProximity,
        weight: supplyFlow.weight,
        weightOption: supplyFlow.weightOption,
        coldChainPercentage: supplyFlow.coldChainPercentage,
        originLocation: supplyFlow.originLocation || '',
        segments: supplyFlow.segments.map((segment) => ({
          nodeName: segment.nodeName || undefined,
          nodeType: segment.nodeType,
          distance: Number(segment.distance || 0),
          transportMode: segment.transportMode || '',
          storageDuration: segment.storageDuration || 'not_stored',
        })),
      });
    }
  }, [form, supplyFlow]);

  return (
    <GoogleMapsApiProvider>
      <Page name="Supply flow" page="guide">
        <div className="flex h-full min-h-screen flex-col bg-gradient-to-r from-primary-100 to-primary-50">
          <Navbar
            logo={{
              variant: 'name',
              color: 'dark',
            }}
            includeCloseButton
            closeButtonProps={{
              onClick: () =>
                navigate(
                  generatePath(
                    supplyChainLogistics?.status === 'unstarted'
                      ? ccfRoutes.SUPPLY_CHAIN_LOGISTICS
                      : ccfRoutes.SUPPLY_CHAIN_LOGISTICS,
                    {
                      report_uuid: companyReportUuid,
                    }
                  )
                ),
            }}
          />
          <div className="grid flex-1 grid-cols-1 overflow-y-auto p-2">
            <Form {...form}>
              <Outlet context={context} />
            </Form>
          </div>
        </div>
      </Page>
    </GoogleMapsApiProvider>
  );
}
